import React, { useEffect, useState } from "react";
import { ProposalResponse } from "../../api/proposal";

interface ProposalDetailsModalProps {
    proposal: ProposalResponse | null;
    isOpen: boolean;
    onClose: () => void;
}

const ProposalDetailsModal: React.FC<ProposalDetailsModalProps> = ({
    proposal,
    isOpen,
    onClose,
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    useEffect(() => {
        if (isOpen) {
            setPosition({ x: 0, y: 0 });
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen]);

    const handleMouseDown = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest("button, a, .btn-close")) return;
        setIsDragging(true);
        setDragStart({
            x: e.clientX - position.x,
            y: e.clientY - position.y,
        });
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return;
        setPosition({
            x: e.clientX - dragStart.x,
            y: e.clientY - dragStart.y,
        });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        if (isDragging) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
        } else {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        }
        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging, dragStart]);

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleEscKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
            onClose();
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.addEventListener("keydown", handleEscKey);
        }
        return () => {
            document.removeEventListener("keydown", handleEscKey);
        };
    }, [isOpen]);

    if (!isOpen || !proposal) return null;

    return (
        <div
            className="modal fade show"
            style={{
                display: "block",
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0, 0, 0, 0.5)",
                zIndex: 1050,
                animation: "fadeIn 0.2s ease-in-out",
            }}
            onClick={handleBackdropClick}
        >
            <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .proposal-details-modal .modal-header {
          cursor: move;
          user-select: none;
          background: white;
          border-bottom: 1px solid #dee2e6;
          padding: 1rem 1.5rem;
        }
        .proposal-details-modal .modal-title {
          color: #dc3545;
          font-weight: 600;
        }
        .custom-btn-close {
          background: transparent;
          border: 1px solid #dc3545 !important;
          color: #dc3545 !important;
          font-size: 1.5rem;
          line-height: 1;
          opacity: 1;
          padding: 0;
          width: 32px;
          height: 32px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .custom-btn-close:hover {
          background: #dc3545 !important;
          color: white !important;
          opacity: 1;
        }
        .custom-btn-close i {
          font-size: 1.25rem;
        }
        .proposal-details-modal .modal-content {
          border: none;
          border-radius: 8px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
          overflow: hidden;
          will-change: transform;
        }
        .proposal-details-modal .modal-dialog {
          transition: transform 0.05s ease-out;
        }
        .proposal-details-modal .modal-body {
          padding: 1.5rem;
        }
        .proposal-details-modal .modal-footer {
          border-top: 1px solid #e9ecef;
          padding: 1rem 1.5rem;
          background: #f8f9fa;
        }
        .info-section {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1rem;
        }
        .info-section h6 {
          color: #495057;
          font-weight: 600;
          margin-bottom: 0.75rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #667eea;
        }
        .info-row {
          display: flex;
          padding: 0.5rem 0;
          border-bottom: 1px solid #e9ecef;
        }
        .info-row:last-child {
          border-bottom: none;
        }
        .info-label {
          font-weight: 600;
          color: #6c757d;
          min-width: 200px;
          flex-shrink: 0;
        }
        .info-value {
          color: #212529;
          word-break: break-word;
        }
      `}</style>

            <div
                className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable proposal-details-modal"
                style={{
                    transform: `translate(${position.x}px, ${position.y}px)`,
                    pointerEvents: "auto",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-content">
                    <div className="modal-header" onMouseDown={handleMouseDown}>
                        <h5 className="modal-title">
                            <i className="ti ti-eye me-2" />
                            Proposal Details
                        </h5>
                        <button
                            type="button"
                            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                            onClick={onClose}
                            aria-label="Close"
                        >
                            <i className="ti ti-x" />
                        </button>
                    </div>

                    <div className="modal-body">
                        {/* Proposal Information */}
                        <div className="info-section">
                            <h6>
                                <i className="ti ti-file-text me-2" />
                                Proposal Information
                            </h6>
                            <div className="info-row">
                                <span className="info-label">Proposal ID:</span>
                                <span className="info-value">{proposal.proposal_id || "N/A"}</span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Proposal Number:</span>
                                <span className="info-value">{proposal.client_proposal_number || "N/A"}</span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Status:</span>
                                <span className="info-value">
                                    <span className={`badge bg-${proposal.status?.toLowerCase() === 'accepted' ? 'success' : proposal.status?.toLowerCase() === 'declined' ? 'danger' : 'warning'}`}>
                                        {proposal.status || "N/A"}
                                    </span>
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Description:</span>
                                <span className="info-value">{proposal.description || "N/A"}</span>
                            </div>
                        </div>

                        {/* Financial Details */}
                        <div className="info-section">
                            <h6>
                                <i className="ti ti-coin me-2" />
                                Financial Details
                            </h6>
                            <div className="info-row">
                                <span className="info-label">Total Amount:</span>
                                <span className="info-value">
                                    {proposal.currency} {proposal.proposal_total_amount?.toLocaleString() || "0"}
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Subtotal:</span>
                                <span className="info-value">
                                    {proposal.currency} {proposal.proposal_amount?.toLocaleString() || "0"}
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Tax:</span>
                                <span className="info-value">
                                    {proposal.currency} {proposal.proposal_tax?.toLocaleString() || "0"} ({proposal.proposal_tax_percentage}%)
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Discount:</span>
                                <span className="info-value">
                                    {proposal.currency} {proposal.proposal_discount?.toLocaleString() || "0"} ({proposal.proposal_discount_percentage}%)
                                </span>
                            </div>
                        </div>

                        {/* Tax Breakdown */}
                        {(proposal.cgst !== undefined || proposal.sgst !== undefined || proposal.igst !== undefined) && (
                            <div className="info-section">
                                <h6>
                                    <i className="bi bi-receipt me-2" />
                                    Tax Breakdown
                                </h6>
                                <div className="info-row">
                                    <span className="info-label">CGST:</span>
                                    <span className="info-value">{proposal.currency} {proposal.cgst || 0} ({proposal.cgst_percentage}%)</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">SGST:</span>
                                    <span className="info-value">{proposal.currency} {proposal.sgst || 0} ({proposal.sgst_percentage}%)</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">IGST:</span>
                                    <span className="info-value">{proposal.currency} {proposal.igst || 0} ({proposal.igst_percentage}%)</span>
                                </div>
                            </div>
                        )}

                        {/* Lead & Contact */}
                        <div className="info-section">
                            <h6>
                                <i className="ti ti-user-check me-2" />
                                Lead & Contact
                            </h6>
                            <div className="info-row">
                                <span className="info-label">Company:</span>
                                <span className="info-value">{proposal.company_name || proposal.leads?.contact?.company_name || "N/A"}</span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Contact Person:</span>
                                <span className="info-value">{ proposal.leads?.contact?.contact_person_name || "N/A"}</span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Product:</span>
                                <span className="info-value">{proposal.product_name || proposal.leads?.products?.product_name || "N/A"}</span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Lead Owner:</span>
                                <span className="info-value">{proposal.leads?.contact?.users?.name || "N/A"}</span>
                            </div>
                        </div>

                        {/* Dates */}
                        <div className="info-section">
                            <h6>
                                <i className="ti ti-calendar me-2" />
                                Dates
                            </h6>
                            <div className="info-row">
                                <span className="info-label">Proposal Date:</span>
                                <span className="info-value">{proposal.Date_of_proposal || "N/A"}</span>
                            </div>
                            <div className="info-row">
                                <span className="info-label">Validation Date:</span>
                                <span className="info-value">{proposal.validation_date || "N/A"}</span>
                            </div>
                        </div>

                        {/* Created/Updated */}
                        {(proposal.created_at || proposal.updated_at) && (
                            <div className="text-center text-muted small mt-3">
                                {proposal.created_at && (
                                    <div>
                                        <i className="ti ti-calendar me-1" />
                                        Created: {new Date(proposal.created_at).toLocaleString()}
                                    </div>
                                )}
                                {proposal.updated_at && (
                                    <div className="mt-1">
                                        <i className="ti ti-clock me-1" />
                                        Updated: {new Date(proposal.updated_at).toLocaleString()}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-light px-4"
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProposalDetailsModal;
